import { useMemo } from 'react'
import { ExternalLink } from 'lucide-react'
import type { CreatorConvergenceAlertRow, CreatorRow } from '../../hooks/useCompetitiveData'
import CreatorConvergenceAlert from './CreatorConvergenceAlert'

interface CreatorRosterPanelProps {
  creators: CreatorRow[]
  convergenceAlerts?: CreatorConvergenceAlertRow[]
  title?: string
  emptyMessage?: string
}

interface CompetitorCreatorRow {
  creator_id: number
  handle: string
  follower_count: number
  follower_tier: string
  is_verified: boolean
  mention_type: string
  mention_count: number
  latest_mentioned_at: string
  latest_video_id: string | null
}

function tierTone(tier: string): { bg: string; border: string; text: string } {
  const normalized = tier.toLowerCase()
  if (normalized === 'mega') {
    return { bg: 'var(--danger-50)', border: 'var(--danger-200)', text: 'var(--danger-700)' }
  }
  if (normalized === 'macro') {
    return { bg: 'var(--warning-50)', border: 'var(--warning-200)', text: 'var(--warning-700)' }
  }
  if (normalized === 'micro') {
    return { bg: 'var(--brand-50)', border: 'var(--brand-200)', text: 'var(--brand-700)' }
  }
  return { bg: 'var(--gray-50)', border: 'var(--gray-200)', text: 'var(--gray-700)' }
}

function mentionTypeTone(type: string): { bg: string; border: string; text: string } {
  const normalized = type.toLowerCase()
  if (normalized === 'paid') {
    return { bg: 'var(--danger-50)', border: 'var(--danger-200)', text: 'var(--danger-700)' }
  }
  if (normalized === 'gifted') {
    return { bg: 'var(--warning-50)', border: 'var(--warning-200)', text: 'var(--warning-700)' }
  }
  if (normalized === 'affiliate') {
    return { bg: 'var(--brand-50)', border: 'var(--brand-200)', text: 'var(--brand-700)' }
  }
  return { bg: 'var(--gray-50)', border: 'var(--gray-200)', text: 'var(--gray-700)' }
}

function formatMentionType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export default function CreatorRosterPanel({
  creators,
  convergenceAlerts = [],
  title = 'Creator Mentions by Competitor',
  emptyMessage = 'No creator mentions detected in the last 30 days.',
}: CreatorRosterPanelProps) {
  const groupedRows = useMemo(() => {
    const competitorMap = new Map<
      string,
      {
        competitorId: number | null
        competitorName: string
        rows: Map<string, CompetitorCreatorRow>
      }
    >()

    for (const creator of creators) {
      for (const mention of creator.recent_mentions || []) {
        const key = `${mention.competitor_id ?? 'unknown'}:${mention.competitor_name}`
        if (!competitorMap.has(key)) {
          competitorMap.set(key, {
            competitorId: mention.competitor_id,
            competitorName: mention.competitor_name,
            rows: new Map(),
          })
        }

        const group = competitorMap.get(key)
        if (!group) {
          continue
        }

        const rowKey = `${creator.id}:${mention.mention_type}`
        const existing = group.rows.get(rowKey)

        if (!existing) {
          group.rows.set(rowKey, {
            creator_id: creator.id,
            handle: creator.handle,
            follower_count: creator.follower_count,
            follower_tier: creator.follower_tier,
            is_verified: creator.is_verified,
            mention_type: mention.mention_type,
              mention_count: 1,
              latest_mentioned_at: mention.mentioned_at,
              latest_video_id: mention.video_id || null,
            })
            continue
          }

          existing.mention_count += 1
          if (new Date(mention.mentioned_at).getTime() > new Date(existing.latest_mentioned_at).getTime()) {
            existing.latest_mentioned_at = mention.mentioned_at
            existing.latest_video_id = mention.video_id || null
          }
        }
      }

    return Array.from(competitorMap.values())
      .map((group) => ({
        ...group,
        rows: Array.from(group.rows.values()).sort((left, right) => {
          if (right.follower_count !== left.follower_count) {
            return right.follower_count - left.follower_count
          }
          return new Date(right.latest_mentioned_at).getTime() - new Date(left.latest_mentioned_at).getTime()
        }),
      }))
      .sort((left, right) => {
        const leftMax = left.rows[0]?.follower_count ?? 0
        const rightMax = right.rows[0]?.follower_count ?? 0
        return rightMax - leftMax
      })
  }, [creators])

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">{title}</h3>

      {convergenceAlerts.length > 0 && (
        <div className="mt-3 space-y-2">
          {convergenceAlerts.slice(0, 3).map((alert) => (
            <CreatorConvergenceAlert
              key={alert.id}
              competitorName={alert.competitor_name}
              creatorCount={alert.creator_count}
              timeWindowDays={7}
            />
          ))}
        </div>
      )}

      {groupedRows.length === 0 ? (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">{emptyMessage}</div>
      ) : (
        <div className="mt-3 space-y-4">
          {groupedRows.map((group) => (
            <article key={`${group.competitorId ?? 'unknown'}-${group.competitorName}`} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">{group.competitorName}</p>
                <span className="text-xs text-slate-500">{group.rows.length} creator rows</span>
              </div>

              <div className="space-y-2">
                {group.rows.map((row) => {
                  const tier = tierTone(row.follower_tier)
                  const mention = mentionTypeTone(row.mention_type)
                  const isLargeCreator = ['macro', 'mega'].includes((row.follower_tier || '').toLowerCase())
                  const normalizedHandle = row.handle.replace(/^@/, '').trim()
                  const profileUrl = normalizedHandle ? `https://www.tiktok.com/@${normalizedHandle}` : null
                  const postUrl =
                    normalizedHandle && row.latest_video_id
                      ? `https://www.tiktok.com/@${normalizedHandle}/video/${row.latest_video_id}`
                      : null
                  const mentionSearchUrl = `https://www.tiktok.com/search?q=${encodeURIComponent(
                    `${group.competitorName} ${normalizedHandle || row.handle}`
                  )}`

                  return (
                    <div
                      key={`${row.creator_id}-${row.mention_type}`}
                      className="rounded-md border p-2.5"
                      style={{
                        borderColor: isLargeCreator ? 'var(--danger-200)' : 'var(--gray-200)',
                        backgroundColor: isLargeCreator ? 'var(--danger-50)' : 'white',
                      }}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {profileUrl ? (
                            <a
                              href={profileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:underline"
                            >
                              @{normalizedHandle || row.handle}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-sm font-semibold text-slate-900">@{row.handle}</span>
                          )}
                          {row.is_verified && (
                            <span className="rounded-full border px-2 py-0.5 text-[11px] font-semibold" style={{ borderColor: 'var(--brand-200)', backgroundColor: 'var(--brand-50)', color: 'var(--brand-700)' }}>
                              Verified
                            </span>
                          )}
                          <span
                            className="rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase"
                            style={{ borderColor: tier.border, backgroundColor: tier.bg, color: tier.text }}
                          >
                            {row.follower_tier}
                          </span>
                          <span
                            className="rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                            style={{ borderColor: mention.border, backgroundColor: mention.bg, color: mention.text }}
                          >
                            {formatMentionType(row.mention_type)}
                          </span>
                        </div>
                        <span className="text-xs text-slate-600">{Math.round(row.follower_count).toLocaleString('en-US')} followers</span>
                      </div>
                      <div className="mt-1.5 text-xs text-slate-600">
                        Mentions: {row.mention_count} · Last seen: {new Date(row.latest_mentioned_at).toLocaleDateString('en-US')}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs">
                        {postUrl ? (
                          <a
                            href={postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-semibold text-blue-600 hover:underline"
                          >
                            View Post
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <a
                            href={mentionSearchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-semibold text-blue-600 hover:underline"
                          >
                            Find Mention
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}
